import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, Input, Select, Textarea, DatePicker, Card, RNLLoader, HeaderBack } from '../components/UI';
import { fmt } from '../utils';
import * as api from '../api';

export default function StandaloneDMREntryPage({ toast }) {
  const navigate = useNavigate();

  // Master Data
  const [projects, setProjects] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accountLedgers, setAccountLedgers] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [costCategories, setCostCategories] = useState([]);

  // Project Specific Data
  const [boqItems, setBoqItems] = useState([]);
  const [subsections, setSubsections] = useState([]);

  // State
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingProjectData, setLoadingProjectData] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({ transaction_date: new Date().toISOString().slice(0, 10), unit_of_measure: 'Nr', currency: 'NGN' });
  const [boqComponents, setBoqComponents] = useState([]);
  const [showAlternateItem, setShowAlternateItem] = useState(false);

  // Load Initial Master Data (Projects, Materials, etc.)
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [projRes, matRes, catRes, supRes, ledgRes, expRes, costCatRes] = await Promise.all([
          api.listProjects(),
          api.listMaterials().catch(() => []),
          api.listDmrCategories().catch(() => []),
          api.listSuppliers().catch(() => []),
          api.listAccountLedgers().catch(() => []),
          api.listExpenseCategories().catch(() => []),
          api.listCategories(false).catch(() => [])
        ]);
        
        setProjects(Array.isArray(projRes) ? projRes : (projRes?.data || projRes?.items || []));
        setMaterials(Array.isArray(matRes) ? matRes : (matRes?.data || matRes?.items || []));
        setCategories(Array.isArray(catRes) ? catRes : (catRes?.data || catRes?.items || []));
        setSuppliers(Array.isArray(supRes) ? supRes.filter(s => s.status === 'active') : []);
        setAccountLedgers(Array.isArray(ledgRes) ? ledgRes : []);
        setExpenseCategories(Array.isArray(expRes) ? expRes : []);
        setCostCategories(Array.isArray(costCatRes) ? costCatRes : []);
      } catch (e) {
        toast?.show?.('Failed to load master data', 'error');
      } finally {
        setLoadingInitial(false);
      }
    };
    loadInitial();
  }, [toast]);

  // Handle Project Selection Change
  useEffect(() => {
    if (!selectedProjectId) {
      setBoqItems([]);
      setSubsections([]);
      return;
    }
    
    const loadProjectData = async () => {
      setLoadingProjectData(true);
      try {
        const [subsRes, boqRes] = await Promise.all([
          api.listDmrSubsections(selectedProjectId).catch(() => []),
          api.listBOQ(selectedProjectId).catch(() => [])
        ]);
        setSubsections(Array.isArray(subsRes) ? subsRes : (subsRes?.data || subsRes?.items || []));
        setBoqItems(Array.isArray(boqRes) ? boqRes : (boqRes?.data || boqRes?.items || []));
      } catch (e) {
        toast?.show?.('Failed to load project-specific data', 'error');
      } finally {
        setLoadingProjectData(false);
      }
    };
    loadProjectData();
  }, [selectedProjectId, toast]);

  // Generate Transaction ID once project is selected and form is empty
  useEffect(() => {
    if (selectedProjectId && !form.transaction_id) {
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      setForm(f => ({ ...f, transaction_id: `DMR-${dateStr}-${randomStr}` }));
    }
  }, [selectedProjectId, form.transaction_id]);

  // Update BOQ Components when Main BOQ Item changes
  useEffect(() => {
    if (form.boq_item_id) {
      const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
      const selectedItem = itemsArray.find(i => String(i.boq_item_id) === String(form.boq_item_id) || String(i.id) === String(form.boq_item_id));
      if (selectedItem && selectedItem.components && selectedItem.components.length > 0) {
        setBoqComponents(selectedItem.components);
      } else {
        setBoqComponents([]);
      }
    } else {
      setBoqComponents([]);
    }
  }, [form.boq_item_id, boqItems]);

  // Frontend Calculations for Totals
  useEffect(() => {
    const qty = parseFloat(form.quantity) || 0;
    const price = parseFloat(form.unit_price) || 0;
    const discount = parseFloat(form.discount_amount) || 0;

    let baseAmount = (qty * price) - discount;

    const vatPercent = parseFloat(form.vat_percent) || 0;
    const whtPercent = parseFloat(form.wht_percent) || 0;
    const retention = parseFloat(form.retention_amount) || 0;
    const mgtFee = parseFloat(form.management_fee) || 0;
    const other = parseFloat(form.other_charges) || 0;

    const vatAmount = baseAmount * (vatPercent / 100);
    const whtAmount = baseAmount * (whtPercent / 100);

    const finalTotal = baseAmount + vatAmount + other + mgtFee - whtAmount - retention;

    if (form.total_amount !== finalTotal.toFixed(2) && finalTotal > 0) {
      setForm(f => ({ ...f, total_amount: finalTotal.toFixed(2) }));
    } else if (finalTotal === 0 && form.total_amount) {
      setForm(f => ({ ...f, total_amount: '' }));
    }
  }, [
    form.quantity, form.unit_price, form.discount_amount,
    form.vat_percent, form.wht_percent, form.retention_amount,
    form.management_fee, form.other_charges
  ]);

  const saveTransaction = async () => {
    if (!selectedProjectId) {
      toast?.show?.('Please select a project first', 'error');
      return;
    }
    if (!form.material_id || !form.quantity || !form.unit_price || !form.dmr_subsection_id || !form.dmr_category_id) {
      toast?.show?.('Please fill in required fields: Sub-section, Category, Material, Quantity, Unit Price', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, project_id: selectedProjectId };
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      
      await api.createMaterialTxn(payload);
      toast?.show?.('Draft material entry created successfully');
      
      // Reset form but keep basic defaults
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      setForm({ 
        transaction_date: new Date().toISOString().slice(0, 10), 
        unit_of_measure: 'Nr', 
        currency: 'NGN',
        transaction_id: `DMR-${dateStr}-${randomStr}` 
      });
      setShowAlternateItem(false);
    } catch (e) {
      toast?.show?.(e.message, 'error');
    }
    setSaving(false);
  };

  if (loadingInitial) {
    return <RNLLoader title="Loading Master Data..." subtitle="Please wait" />;
  }

  const sectionTitleStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', marginBottom: 12, textTransform: 'uppercase' };
  const inputStyle = { background: '#f8fafc', color: '#1e293b', border: '1px solid #cbd5e1', padding: '10px 12px' };
  
  const boqItemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);

  return (
    <div className="fade-in" style={{ padding: '10px 0', maxWidth: '100%', margin: '0 auto', width: '100%' }}>
      {/* Back button moved to top header */}
      <HeaderBack onClick={() => navigate('/report-entry')} label="Back to Report Entry" />

      <div style={{ background: '#ffffff', border: '3px solid black', borderRadius: 22, padding: 18, boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ borderRadius: 12, overflow: 'hidden', background: '#ffffff', border: '1px solid black' }}>
        {/* PROJECT SELECTION BAR & TITLE */}
        <div style={{ background: '#0b318bff', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#ffffff', letterSpacing: '0.02em' }}>
            New Direct Material Entry
          </h2>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {loadingProjectData && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginRight: 8 }}>Loading project data...</div>}
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap' }}>SELECT PROJECT:</div>
            <div style={{ width: 280 }}>
              <Select 
                value={selectedProjectId} 
                onChange={setSelectedProjectId}
                options={[{ value: '', label: '— Select Project Context —' }, ...projects.map(p => ({ value: p.project_id, label: p.project_name }))]} 
                style={{ background: '#fff', border: 'none', borderRadius: 8, height: 38 }}
              />
            </div>
          </div>
        </div>

        {!selectedProjectId ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏢</div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>No Project Selected</h3>
            <p>Please select a project from the dropdown above to continue with the entry.</p>
          </div>
        ) : (
          <div className="mobile-p-16" style={{ padding: '32px', background: '#ffffff' }}>
            
            {/* SECTION 1 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 1 — BOQ LINE CLASSIFICATION</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                <Select label="MAIN BOQ ITEM" value={form.boq_item_id || ''}
                  onChange={v => setForm(f => ({ ...f, boq_item_id: v, boq_component_id: '' }))}
                  style={inputStyle}
                  options={[{ value: '', label: '— Select BOQ Item —' }, ...boqItemsArray.map(i => ({ value: i.boq_item_id || i.id, label: `${i.boq_code || ''} — ${i.description}`.trim() }))]} />

                <Select label="SUB BOQ COMPONENT" value={form.boq_component_id || ''}
                  onChange={v => setForm(f => ({ ...f, boq_component_id: v }))}
                  style={inputStyle}
                  disabled={boqComponents.length === 0}
                  options={boqComponents.length === 0
                    ? [{ value: '', label: '— No Components Available —' }]
                    : [{ value: '', label: '— Select Component —' }, ...boqComponents.map(c => ({ value: c.component_id || c.id, label: c.description || c.name }))]
                  } />

                <Select label="COST COMPONENT" value={form.cost_category_id || ''}
                  onChange={v => setForm(f => ({ ...f, cost_category_id: v }))}
                  style={inputStyle}
                  options={[{ value: '', label: '— Select Cost Component —' }, ...(costCategories || []).map(c => ({ value: c.cost_category_id, label: c.name }))]} />
              </div>
            </div>

            {/* SECTION 2 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 2 — GENERAL DETAILS</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                <DatePicker label="ENTRY DATE" required value={form.transaction_date || ''}
                  onChange={v => setForm(f => ({ ...f, transaction_date: v }))} style={inputStyle} />

                <div />

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 'bold', color: '#000101ff', marginBottom: 4, textTransform: 'uppercase' }}>TRANSACTION ID</label>
                  <div style={{ background: '#e2e8f0', border: '2px dashed #081220ff', borderRadius: '6px', padding: '9px 12px', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                      {form.transaction_id || 'Generating ID...'}
                    </span>
                  </div>
                </div>

                <Select label="PAYMENT MODE" value={form.payment_mode || ''}
                  onChange={v => setForm(f => ({ ...f, payment_mode: v }))}
                  style={inputStyle}
                  options={[
                    { value: '', label: '— Select Payment Mode —' },
                    { value: 'Bank Transfer', label: 'Bank Transfer' },
                    { value: 'Cash', label: 'Cash' },
                    { value: 'Cheque', label: 'Cheque' },
                    { value: 'POS', label: 'POS' },
                    { value: 'Mobile money', label: 'Mobile money' },
                    { value: 'Credit (30 days)', label: 'Credit (30 days)' },
                    { value: 'Credit (60 days)', label: 'Credit (60 days)' },
                  ]} />
                <Select label="SUB-SECTION" required value={form.dmr_subsection_id || ''}
                  onChange={v => setForm(f => ({ ...f, dmr_subsection_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Sub-section —' }, ...subsections.map(b => ({ value: b.id, label: b.name }))]} />
                <Select label="CATEGORY" required value={form.dmr_category_id || ''}
                  onChange={v => setForm(f => ({ ...f, dmr_category_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Category —' }, ...categories.map(c => ({ value: c.id, label: c.name }))]} />

                <Select label="SUPPLIER" value={form.supplier_id || ''}
                  onChange={v => setForm(f => ({ ...f, supplier_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Supplier —' }, ...(suppliers || []).map(s => ({ value: s.supplier_id, label: s.name }))]} />
                <Input label="MANUAL NO" value={form.manual_no || ''}
                  onChange={v => setForm(f => ({ ...f, manual_no: v }))} style={inputStyle} />
                <Input label="INVOICE NO" value={form.invoice_no || ''}
                  onChange={v => setForm(f => ({ ...f, invoice_no: v }))} style={inputStyle} />

                <Input label="WAYBILL NO" value={form.waybill_no || ''}
                  onChange={v => setForm(f => ({ ...f, waybill_no: v }))} style={inputStyle} />
                <Input label="VOUCHER NO" value={form.voucher_no || ''}
                  onChange={v => setForm(f => ({ ...f, voucher_no: v }))} style={inputStyle} />
                <Input label="REFERENCE NO" value={form.reference_no || ''}
                  onChange={v => setForm(f => ({ ...f, reference_no: v }))} style={inputStyle} />
              </div>
            </div>

            {/* SECTION 3 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 3 — MATERIAL LINE & FINANCIALS</div>
              <div className="grid-cols-4" style={{ background: '#f0f4f8', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                <Select label="MATERIAL" required value={form.material_id || ''}
                  onChange={v => setForm(f => ({ ...f, material_id: v }))}
                  style={{ ...inputStyle, gridColumn: 'span 2' }}
                  options={[{ value: '', label: '— Select Material —' }, ...materials.map(m => ({ value: m.material_id, label: m.name }))]} />
                <Select label="ACCOUNT LEDGER" value={form.account_ledger_id || ''}
                  onChange={v => setForm(f => ({ ...f, account_ledger_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Account Ledger —' }, ...(accountLedgers || []).map(a => ({ value: a.ledger_id, label: a.name }))]} />
                <Select label="EXPENSE CODE" value={form.expense_category_id || ''}
                  onChange={v => setForm(f => ({ ...f, expense_category_id: v }))} style={inputStyle}
                  options={[{ value: '', label: '— Select Expense Code —' }, ...(expenseCategories || []).map(e => ({ value: e.expense_category_id, label: e.name }))]} />

                <Input label="QUANTITY" type="number" required value={form.quantity || ''}
                  onChange={v => setForm(f => ({ ...f, quantity: v }))} style={inputStyle} />
                <Select label="UNIT OF MEASURE" required value={form.unit_of_measure || 'Nr'}
                  onChange={v => setForm(f => ({ ...f, unit_of_measure: v }))} style={inputStyle}
                  options={['M', 'M2', 'M3', 'Nr', 'KG'].map(s => ({ value: s, label: s }))} />
                <Input label="UNIT PRICE" type="number" required value={form.unit_price || ''}
                  onChange={v => setForm(f => ({ ...f, unit_price: v }))} style={inputStyle} />
                <Select label="CURRENCY" value={form.currency || 'NGN'}
                  onChange={v => setForm(f => ({ ...f, currency: v }))} style={inputStyle}
                  options={['NGN', 'USD', 'EUR', 'GBP'].map(s => ({ value: s, label: s }))} />

                <Input label="DISCOUNT AMT" type="number" value={form.discount_amount || ''}
                  onChange={v => setForm(f => ({ ...f, discount_amount: v }))} style={inputStyle} />
                <Input label="VAT %" type="number" value={form.vat_percent || ''}
                  onChange={v => setForm(f => ({ ...f, vat_percent: v }))} style={inputStyle} />
                <Input label="VAT WITHHELD" type="number" value={form.vat_witheld || ''}
                  onChange={v => setForm(f => ({ ...f, vat_witheld: v }))} style={inputStyle} />
                <Input label="WHT %" type="number" value={form.wht_percent || ''}
                  onChange={v => setForm(f => ({ ...f, wht_percent: v }))} style={inputStyle} />

                <Input label="RETENTION AMT" type="number" value={form.retention_amount || ''}
                  onChange={v => setForm(f => ({ ...f, retention_amount: v }))} style={inputStyle} />
                <Input label="MANAGEMENT FEE" type="number" value={form.management_fee || ''}
                  onChange={v => setForm(f => ({ ...f, management_fee: v }))} style={inputStyle} />
                <Input label="OTHER CHARGES" type="number" value={form.other_charges || ''}
                  onChange={v => setForm(f => ({ ...f, other_charges: v }))} style={inputStyle} />
                <Input label="USD AMOUNT" type="number" value={form.usd_amount || ''}
                  onChange={v => setForm(f => ({ ...f, usd_amount: v }))} style={inputStyle} />

                <Input label="BRAND NAME" value={form.brand_name || ''}
                  onChange={v => setForm(f => ({ ...f, brand_name: v }))} style={{ ...inputStyle, gridColumn: 'span 2' }} />

                <div style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#1e293b', borderRadius: '8px', padding: '12px 16px', color: '#fff' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>CALCULATED TOTAL AMOUNT</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#10b981', fontFamily: 'var(--mono)' }}>{fmt(form.total_amount || 0)}</span>
                </div>
              </div>
            </div>

            {/* SECTION 4 */}
            <div>
              <div style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span>SECTION 4 — ALTERNATIVE ITEM & REMARKS</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textTransform: 'none', color: '#1e293b', cursor: 'pointer' }}>
                  <input type="checkbox" checked={showAlternateItem} onChange={e => setShowAlternateItem(e.target.checked)} />
                  Include Alternate Item Details
                </label>
              </div>

              <div className="grid-cols-2" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid black' }}>
                {showAlternateItem && (
                  <>
                    <Input label="ALTERNATIVE ITEM NAME" value={form.alternative_item_name || ''}
                      onChange={v => setForm(f => ({ ...f, alternative_item_name: v }))} style={inputStyle} />
                    <Input label="REASON FOR USING ALTERNATIVE" value={form.reason_for_using_alternative_item || ''}
                      onChange={v => setForm(f => ({ ...f, reason_for_using_alternative_item: v }))} style={inputStyle} />
                  </>
                )}

                <div style={{ gridColumn: 'span 2' }}>
                  <Textarea label="REMARKS" value={form.remarks || ''}
                    onChange={v => setForm(f => ({ ...f, remarks: v }))}
                    rows={3} placeholder="Optional notes or description about the entry..."
                    style={{ ...inputStyle, width: '100%' }} />
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="mobile-form-footer flex-mobile-col" style={{ background: '#0b318bff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '32px -32px -32px -32px' }}>
              <Btn onClick={() => navigate('/report-entry')} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }}>
                Cancel
              </Btn>
              <Btn loading={saving} onClick={saveTransaction} disabled={!selectedProjectId} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }}>
                Save
              </Btn>
            </div>

          </div>
        )}
        </div>
      </div>
    </div>
  );
}
